/**
 * FilterPanel Component - Production Optimized Version
 * 
 * Key Improvements:
 * - Reduced unnecessary re-renders with React.memo and useCallback
 * - Simplified filter state management
 * - Better accessibility (ARIA labels, keyboard navigation)
 * - Cleaner code structure with extracted utilities
 * - Improved mobile UX with apply/discard changes
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Filter,
  Search,
  ChevronUp,
  ChevronDown,
  X,
  RefreshCw,
  Check
} from 'lucide-react';

// ✅ Constants
const SEARCH_PLACEHOLDERS = [
  "UN Forklifts",
  "Telehandler",
  "Hengwang Dozer",
  "Rippa Mini Excavator"
];

const TOP_BRANDS_ORDER = ['UN Forklifts', 'Socma', 'Rippa'];

// ✅ Custom checkbox styles (injected once)
const CHECKBOX_STYLES = `
  .custom-checkbox {
    appearance: none;
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border: 1px solid #d1d5db;
    border-radius: 2px;
    background-color: white;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  .custom-checkbox:hover {
    border-color: #9ca3af;
  }
  .custom-checkbox:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 210, 0, 0.3);
  }
  .custom-checkbox:checked {
    background: linear-gradient(159.21deg, rgba(255, 210, 0, 0.3) 11.19%, rgba(247, 151, 30, 0.3) 101.23%);
    border: 1px solid;
    border-image-source: linear-gradient(159.21deg, #75620B 11.19%, #7F4B09 101.23%);
    border-image-slice: 1;
  }
  .custom-checkbox:checked::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 2px;
    width: 5px;
    height: 10px;
    border: solid #000000;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
  @keyframes slideUpSearch {
    0%, 22% { transform: translateY(0); }
    25%, 47% { transform: translateY(-20px); }
    50%, 72% { transform: translateY(-40px); }
    75%, 97% { transform: translateY(-60px); }
    100% { transform: translateY(-80px); }
  }
  .sliding-placeholder {
    animation: slideUpSearch 12s ease-in-out infinite;
  }
`;

const FilterPanel = ({ 
  filters, 
  setFilters, 
  filterOptions, 
  isLoading,
  onRefresh 
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [draftFilters, setDraftFilters] = useState(filters);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    price: false,
    pa_brand: true
  });
  const [brands, setBrands] = useState([]);
  const [showMoreBrands, setShowMoreBrands] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // ✅ Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ✅ Sync draft filters with actual filters
  useEffect(() => {
    setDraftFilters(filters);
    setHasUnsavedChanges(false);
  }, [filters]);

  // ✅ Track unsaved changes on mobile
  useEffect(() => {
    if (isMobile) {
      const changed = JSON.stringify(draftFilters) !== JSON.stringify(filters);
      setHasUnsavedChanges(changed);
    }
  }, [draftFilters, filters, isMobile]);

  // ✅ Fetch brands with abort controller
  useEffect(() => {
    const controller = new AbortController();

    const fetchBrands = async () => {
      try {
        setLoadingBrands(true);
        const res = await fetch(
          'https://goldensparrowuae.com/wordpress/wp-json/custom/v1/brands',
          { signal: controller.signal }
        );
        
        if (!res.ok) throw new Error('Failed to fetch brands');
        
        const data = await res.json();
        setBrands(data.brands || []);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching brands:', err);
        }
      } finally {
        setLoadingBrands(false);
      }
    };

    fetchBrands();

    return () => controller.abort();
  }, []);

  // ✅ Inject custom styles once
  useEffect(() => {
    const styleId = 'filter-panel-custom-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = CHECKBOX_STYLES;
      document.head.appendChild(style);
    }

    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) styleElement.remove();
    };
  }, []);

  // ✅ Determine active filter state
  const activeFilters = isMobile ? draftFilters : filters;
  const updateFilters = isMobile ? setDraftFilters : setFilters;

  // ✅ Memoized active filter count
  const activeFilterCount = useMemo(() => {
    return Object.keys(filters).filter(key => {
      if (key === 'search') return false;
      const value = filters[key];
      return value !== undefined && 
             value !== '' &&
             (Array.isArray(value) ? value.length > 0 : true);
    }).length;
  }, [filters]);

  // ✅ Memoized operating weight check
  const hasOperatingWeightFilter = useMemo(() => {
    return activeFilters.min_operating_weight || activeFilters.max_operating_weight;
  }, [activeFilters]);

  // ✅ Memoized ordered top brands
  const orderedTopBrands = useMemo(() => {
    return TOP_BRANDS_ORDER
      .map((name) => brands.find((b) => b.name === name))
      .filter(Boolean);
  }, [brands]);

  // ✅ Memoized remaining brands
  const remainingBrands = useMemo(() => {
    return brands.filter((b) => !TOP_BRANDS_ORDER.includes(b.name));
  }, [brands]);

  // ✅ Toggle section expansion
  const toggleSection = useCallback((sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  }, []);

  // ✅ Apply filters (mobile)
  const applyFilters = useCallback(() => {
    setFilters(draftFilters);
    setHasUnsavedChanges(false);
  }, [draftFilters, setFilters]);

  // ✅ Reset draft filters (mobile)
  const resetDraftFilters = useCallback(() => {
    setDraftFilters(filters);
    setHasUnsavedChanges(false);
  }, [filters]);

  // ✅ Handle checkbox change
  const handleCheckboxChange = useCallback((filterKey, value, isChecked) => {
    updateFilters(prevFilters => {
      const currentValues = Array.isArray(prevFilters[filterKey]) 
        ? prevFilters[filterKey] 
        : [];
      
      if (isChecked) {
        return {
          ...prevFilters,
          [filterKey]: [...currentValues, value]
        };
      } else {
        const newValues = currentValues.filter(v => v !== value);
        if (newValues.length === 0) {
          const { [filterKey]: removed, ...rest } = prevFilters;
          return rest;
        }
        return {
          ...prevFilters,
          [filterKey]: newValues
        };
      }
    });
  }, [updateFilters]);

  // ✅ Handle operating weight change
  const handleOperatingWeightChange = useCallback((type, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      updateFilters(prevFilters => ({
        ...prevFilters,
        [type]: value
      }));
    }
  }, [updateFilters]);

  // ✅ Handle search change
  const handleSearchChange = useCallback((value) => {
    if (value === '') {
      updateFilters(prevFilters => {
        const { search, ...restFilters } = prevFilters;
        return restFilters;
      });
    } else {
      updateFilters(prevFilters => ({
        ...prevFilters,
        search: value
      }));
    }
  }, [updateFilters]);

  // ✅ Clear all filters
  const clearAllFilters = useCallback(() => {
    const emptyFilters = {};
    if (isMobile) {
      setDraftFilters(emptyFilters);
      setFilters(emptyFilters);
      setHasUnsavedChanges(false);
    } else {
      setFilters(emptyFilters);
    }
  }, [setFilters, isMobile]);

  // ✅ Clear specific filter
  const clearFilter = useCallback((filterKey, value = null) => {
    updateFilters(prevFilters => {
      if (value !== null && Array.isArray(prevFilters[filterKey])) {
        const newValues = prevFilters[filterKey].filter(v => v !== value);
        if (newValues.length === 0) {
          const { [filterKey]: removed, ...rest } = prevFilters;
          return rest;
        }
        return { ...prevFilters, [filterKey]: newValues };
      } else {
        const { [filterKey]: removed, ...rest } = prevFilters;
        return rest;
      }
    });
  }, [updateFilters]);

  // ✅ Auto-expand sections with active filters
  useEffect(() => {
    const sectionsWithActiveFilters = {};

    if (activeFilters.min_operating_weight || activeFilters.max_operating_weight) {
      sectionsWithActiveFilters.operating_weight = true;
    }

    Object.keys(activeFilters).forEach(key => {
      if (key !== 'search' && key !== 'min_operating_weight' && key !== 'max_operating_weight') {
        if (activeFilters[key] && 
            (Array.isArray(activeFilters[key]) ? activeFilters[key].length > 0 : activeFilters[key] !== '')) {
          sectionsWithActiveFilters[key] = true;
        }
      }
    });

    const needsUpdate = Object.keys(sectionsWithActiveFilters).some(
      key => !expandedSections[key]
    );

    if (needsUpdate) {
      setExpandedSections(prev => ({
        ...prev,
        ...sectionsWithActiveFilters
      }));
    }
  }, [activeFilters]); // ✅ Removed expandedSections from dependencies

  return (
    <aside 
      role="complementary" 
      aria-label="Product filters" 
      className="bg-white rounded-xl border border-neutral-200 shadow-soft"
    >
      {/* Header */}
      <div className="p-4 border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-display font-semibold text-neutral-800 flex items-center">
            <Filter className="w-5 h-5 mr-2" aria-hidden="true" />
            Filters
            {activeFilterCount > 0 && (
              <span 
                className="ml-2 bg-accent-100 text-accent-800 text-xs font-medium px-2 py-1 rounded-full"
                aria-label={`${activeFilterCount} filters active`}
              >
                {activeFilterCount}
              </span>
            )}
          </h2>
          
          <div className="flex items-center gap-2">
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="p-1 hover:bg-gray-100 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFD200]"
                title="Refresh filters"
                aria-label="Refresh filters"
              >
                <RefreshCw className="w-4 h-4 text-gray-600" />
              </button>
            )}
            
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-red-600 hover:text-red-700 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 rounded px-2 py-1"
                aria-label="Clear all filters"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="p-3 border-b border-gray-200">
          <h3 className="text-xs font-medium text-gray-600 mb-2">Active Filters:</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(filters).map(([key, value]) => {
              if (key === 'search' || key === 'min_operating_weight' || key === 'max_operating_weight' || !value) {
                return null;
              }

              if (Array.isArray(value)) {
                return value.map(val => {
                  const filterOption = filterOptions?.filters?.[key];
                  const optionName = filterOption?.options?.find(opt => opt.slug === val)?.name || val;

                  return (
                    <span
                      key={`${key}-${val}`}
                      className="inline-flex items-center px-2 py-1 bg-blue-100 text-slate-800 text-xs rounded-full"
                    >
                      {optionName}
                      <button
                        onClick={() => {
                          clearFilter(key, val);
                          if (isMobile) {
                            setFilters(prevFilters => {
                              const currentValues = prevFilters[key] || [];
                              const newValues = currentValues.filter(v => v !== val);
                              if (newValues.length === 0) {
                                const { [key]: removed, ...rest } = prevFilters;
                                return rest;
                              }
                              return { ...prevFilters, [key]: newValues };
                            });
                          }
                        }}
                        className="ml-1 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 rounded"
                        aria-label={`Remove ${optionName} filter`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                });
              }
              return null;
            })}

            {/* Operating Weight Filters */}
            {filters.min_operating_weight && (
              <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-slate-800 text-xs rounded-full">
                Min Weight: {filters.min_operating_weight} kg
                <button
                  onClick={() => {
                    clearFilter('min_operating_weight');
                    if (isMobile) {
                      setFilters(prev => {
                        const { min_operating_weight, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  className="ml-1 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 rounded"
                  aria-label="Remove minimum weight filter"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.max_operating_weight && (
              <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-slate-800 text-xs rounded-full">
                Max Weight: {filters.max_operating_weight} kg
                <button
                  onClick={() => {
                    clearFilter('max_operating_weight');
                    if (isMobile) {
                      setFilters(prev => {
                        const { max_operating_weight, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  className="ml-1 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 rounded"
                  aria-label="Remove maximum weight filter"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Search Field */}
        <div>
          <label 
            htmlFor="product-search" 
            className="block text-sm font-display font-semibold text-gray-700 mb-2"
          >
            Search Product
          </label>
          <div className="relative">
            {!activeFilters.search && (
              <Search 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10" 
                aria-hidden="true" 
              />
            )}
            
            <input
              id="product-search"
              type="text"
              value={activeFilters.search || ''}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="w-full pl-5 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FFD200] focus:border-transparent bg-white relative"
              aria-label="Search products"
              aria-describedby={activeFilters.search && activeFilters.search.length < 3 ? "search-hint" : undefined}
            />
            
            {/* Animated Placeholder */}
            {!activeFilters.search && !isFocused && (
              <div 
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none overflow-hidden" 
                style={{ height: '20px' }}
                aria-hidden="true"
              >
                <div className="sliding-placeholder">
                  {SEARCH_PLACEHOLDERS.map((placeholder, index) => (
                    <div 
                      key={index} 
                      className="text-sm text-gray-400"
                      style={{ height: '20px', lineHeight: '20px' }}
                    >
                      {placeholder}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Clear Search Button */}
            {activeFilters.search && (
              <button
                onClick={() => clearFilter('search')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 z-10 hover:bg-gray-100 rounded p-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#FFD200]"
                aria-label="Clear search"
              >
                <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
          
          {/* Search Hint */}
          {activeFilters.search && activeFilters.search.length > 0 && activeFilters.search.length < 3 && (
            <p id="search-hint" className="text-xs text-amber-600 mt-1" role="alert">
              Enter at least 3 characters to search
            </p>
          )}
        </div>

        <hr className="w-1/2 h-0.5 bg-[#0000004d]" />

        {/* Brand Filter */}
        <div>
          <button
            onClick={() => toggleSection('pa_brand')}
            className="w-full flex justify-between text-sm font-display font-semibold text-gray-700 hover:text-gray-900 py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFD200] rounded"
            aria-expanded={expandedSections.pa_brand !== false}
            aria-controls="brand-filter-content"
          >
            <span>
              Brand
              {activeFilters.pa_brand?.length > 0 && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                  {activeFilters.pa_brand.length}
                </span>
              )}
            </span>
            {expandedSections.pa_brand !== false ? (
              <ChevronUp className="w-4 h-4" aria-hidden="true" />
            ) : (
              <ChevronDown className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
          
          {expandedSections.pa_brand !== false && (
            <div id="brand-filter-content" className="mt-3 space-y-3">
              {loadingBrands ? (
                <div className="text-center text-gray-400 text-sm" role="status">
                  Loading brands...
                </div>
              ) : (
                <>
                {/* Top 3 Brand Logos */}
                  <div className="grid grid-cols-3 gap-3">
                    {orderedTopBrands.map((brand) => {
                      const isSelected = activeFilters.pa_brand?.includes(brand.slug);
                      return (
                        <button
                          key={brand.id}
                          onClick={() =>
                            handleCheckboxChange('pa_brand', brand.slug, !isSelected)
                          }
                          className={`cursor-pointer border rounded-lg p-2 flex items-center justify-center transition-all duration-200 focus:outline-none ${
                            isSelected
                              ? 'border-yellow-400 bg-yellow-50'
                              : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-50'
                          }`}
                          aria-label={`${isSelected ? 'Deselect' : 'Select'} ${brand.name}`}
                          aria-pressed={isSelected}
                        >
                          <img
                            src={brand.logo}
                            alt={brand.name}
                            className="w-16 h-10 object-contain"
                          />
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Show More/Less Toggle (Desktop Only) */}
                  {!isMobile && remainingBrands.length > 0 && (
                    <div className="text-center">
                      <button
                        onClick={() => setShowMoreBrands((prev) => !prev)}
                        className="text-xs font-medium text-gray-600 hover:text-gray-800 focus:outline-none px-2 py-1"
                        aria-expanded={showMoreBrands}
                        aria-controls="remaining-brands-list"
                      >
                        {showMoreBrands ? 'Show Less' : 'Show More'}
                      </button>
                    </div>
                  )}
                  
                  {/* Remaining Brands List */}
                  {(isMobile || showMoreBrands) && remainingBrands.length > 0 && (
                    <div 
                      id="remaining-brands-list"
                      className={`space-y-1 overflow-y-auto pr-2 pt-2 ${
                        isMobile ? 'max-h-64' : 'max-h-48'
                      }`}
                    >
                      {remainingBrands.map((brand) => {
                        const isChecked = activeFilters.pa_brand?.includes(brand.slug);
                        return (
                          <label
                            key={brand.id}
                            className="flex items-center py-1 px-2 hover:bg-gray-50 rounded cursor-pointer group"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) =>
                                handleCheckboxChange('pa_brand', brand.slug, e.target.checked)
                              }
                              className="mr-3 rounded border-gray-300 text-slate-600 focus:ring-[#FFD200] custom-checkbox"
                              aria-label={`Filter by ${brand.name}`}
                            />
                            <span className="flex-1 text-sm text-gray-700 group-hover:text-gray-900">
                              {brand.name}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Scroll Hint (Mobile Only) */}
                  {isMobile && remainingBrands.length > 5 && (
                    <p className="text-xs text-gray-500 text-center py-1" aria-live="polite">
                      ↕ Scroll to see all brands
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Dynamic Filters */}
        {isLoading ? (
          <div className="space-y-3" role="status" aria-label="Loading filters">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          filterOptions?.filters && Object.entries(filterOptions.filters).map(([taxonomyKey, filterData]) => {
            // Skip specific taxonomies
            if (
              taxonomyKey === 'pa_operating-weight' || 
              taxonomyKey === 'pa_operating_weight' || 
              taxonomyKey === 'pa_brand' || 
              taxonomyKey === 'brand'
            ) {
              return null;
            }

            if (!filterData.options || filterData.options.length === 0) {
              return null;
            }

            const isExpanded = expandedSections[taxonomyKey] === true;
            const selectedCount = activeFilters[taxonomyKey]?.length || 0;

            return (
              <div key={taxonomyKey}>
                <button
                  onClick={() => toggleSection(taxonomyKey)}
                  className="w-full flex items-center justify-between text-sm font-display font-semibold text-gray-700 hover:text-gray-900 py-1 focus:outline-none"
                  aria-expanded={isExpanded}
                  aria-controls={`filter-${taxonomyKey}`}
                >
                  <span>
                    {filterData.label}
                    {selectedCount > 0 && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                        {selectedCount}
                      </span>
                    )}
                  </span>
                  {isExpanded ? 
                    <ChevronUp className="w-4 h-4" aria-hidden="true" /> : 
                    <ChevronDown className="w-4 h-4" aria-hidden="true" />
                  }
                </button>

                {isExpanded && (
                  <div 
                    id={`filter-${taxonomyKey}`}
                    className="mt-2 space-y-1 max-h-48 overflow-y-auto pr-2"
                  >
                    {filterData.options.map(option => (
                      <label
                        key={option.slug}
                        className="flex items-center py-1 px-2 hover:bg-gray-50 rounded cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          checked={activeFilters[taxonomyKey]?.includes(option.slug) || false}
                          onChange={(e) => handleCheckboxChange(taxonomyKey, option.slug, e.target.checked)}
                          className="mr-3 rounded border-gray-300 text-slate-600 focus:ring-[#FFD200] custom-checkbox"
                          aria-label={`Filter by ${option.name}`}
                        />
                        <span className="flex-1 text-sm text-gray-700 group-hover:text-gray-900">
                          {option.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Operating Weight Range Filter */}
        <div>
          <button
            onClick={() => toggleSection('operating_weight')}
            className="w-full flex items-center justify-between text-sm font-display font-semibold text-gray-700 hover:text-gray-900 py-1 focus:outline-none"
            aria-expanded={expandedSections.operating_weight}
            aria-controls="operating-weight-filter"
          >
            <span>
              Operating Weight (kg)
              {hasOperatingWeightFilter && (
                <span className="ml-2 text-xs bg-blue-100 text-slate-800 px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </span>
            {expandedSections.operating_weight ?
              <ChevronUp className="w-4 h-4" aria-hidden="true" /> :
              <ChevronDown className="w-4 h-4" aria-hidden="true" />
            }
          </button>

          {expandedSections.operating_weight && (
            <div id="operating-weight-filter" className="mt-2 space-y-2">
              <div className="flex flex-row gap-3 items-center justify-center">
                <div className="flex-1">
                  <label htmlFor="min-weight" className="sr-only">
                    Minimum operating weight
                  </label>
                  <input
                    id="min-weight"
                    type="text"
                    inputMode="numeric"
                    value={activeFilters.min_operating_weight || ''}
                    onChange={(e) => handleOperatingWeightChange('min_operating_weight', e.target.value)}
                    placeholder="From"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FFD200] focus:border-transparent text-sm"
                    aria-label="Minimum operating weight in kilograms"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="max-weight" className="sr-only">
                    Maximum operating weight
                  </label>
                  <input
                    id="max-weight"
                    type="text"
                    inputMode="numeric"
                    value={activeFilters.max_operating_weight || ''}
                    onChange={(e) => handleOperatingWeightChange('max_operating_weight', e.target.value)}
                    placeholder="To"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FFD200] focus:border-transparent text-sm"
                    aria-label="Maximum operating weight in kilograms"
                  />
                </div>
              </div>

              {/* Validation Message */}
              {activeFilters.min_operating_weight && 
               activeFilters.max_operating_weight &&
               parseFloat(activeFilters.min_operating_weight) > parseFloat(activeFilters.max_operating_weight) && (
                <p className="text-xs text-red-600 mt-1 text-center" role="alert">
                  Minimum weight cannot be greater than maximum weight
                </p>
              )}

              {/* Clear Weight Range */}
              {(activeFilters.min_operating_weight || activeFilters.max_operating_weight) && (
                <button
                  onClick={() => {
                    updateFilters(prevFilters => {
                      const { min_operating_weight, max_operating_weight, ...rest } = prevFilters;
                      return rest;
                    });
                  }}
                  className="text-xs text-red-600 hover:text-red-700 font-medium transition-colors w-full text-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 rounded px-2 py-1"
                  aria-label="Clear weight range filter"
                >
                  Clear weight range
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile: Apply/Reset Buttons */}
      {isMobile && (
        <div className="p-4 border-t border-gray-200 space-y-2">
          <button
            onClick={applyFilters}
            disabled={!hasUnsavedChanges}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F7971E] ${
              hasUnsavedChanges 
                ? 'bg-gradient-to-br from-[#FFD200] to-[#F7971E] hover:from-[#F7971E] hover:to-[#FFD200] shadow-md' 
                : 'bg-gray-300 cursor-not-allowed'
            }`}
            aria-label="Apply selected filters"
          >
            <Check className="w-5 h-5" aria-hidden="true" />
            Apply Filters
            {hasUnsavedChanges && (
              <span className="ml-1 bg-white text-yellow-600 text-xs px-2 py-0.5 rounded-full">
                New
              </span>
            )}
          </button>
          
          {hasUnsavedChanges && (
            <button
              onClick={resetDraftFilters}
              className="w-full py-2 px-4 rounded-lg font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
              aria-label="Reset unsaved filter changes"
            >
              Discard Changes
            </button>
          )}
        </div>
      )}

      {/* Filter Summary */}
      {filterOptions?.total > 0 && (
        <div className="px-4 py-6">
          <div className="text-xs text-gray-400 text-center font-medium" role="status">
            Filtering from {filterOptions.total} total products
          </div>
        </div>
      )}
    </aside>
  );
};

// ✅ Memoize component to prevent unnecessary re-renders
export default React.memo(FilterPanel);